import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { SAMPLES } from '../../data/atomizerSamples';
import { useAtomizer } from './useAtomizer';
const FORCE_CLASS = {
  MUST: 'must',
  NEVER: 'never',
  SHOULD: 'should'
} as const;

/** Counts from 0 to `to` over ~600ms; jumps straight there under reduced motion. */
function useCountUp(to: number) {
  const [n, setN] = useState(to);
  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      setN(to);
      return;
    }
    let raf = 0;
    const start = performance.now();
    const tick = (now: number) => {
      const p = Math.min(1, (now - start) / 600);
      setN(Math.round(to * (1 - Math.pow(1 - p, 3))));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    setN(0);
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [to]);
  return n;
}
function plural(n: number, one: string, many: string) {
  return `${n} ${n === 1 ? one : many}`;
}

/**
 * Samples only for now. Upload and paste come back once the platform exposes a
 * public extraction endpoint; runText/runFile stay in useAtomizer for that.
 */
export function Atomizer() {
  const {
    result,
    message,
    libOpen,
    setLibOpen,
    send,
    runSample,
    submitLibrary
  } = useAtomizer();
  const [fields, setFields] = useState({
    name: '',
    email: '',
    company: ''
  });
  const total = result?.total ?? 0;
  const shownTotal = useCountUp(total);
  const shown = result?.rows.length ?? 0;
  return <div id="atomizer-field" style={{
    display: 'flex',
    flexDirection: 'column'
  }} data-id="element-82" data-mp="28hcvx9ya0yqu">
      <div className="picker" data-id="element-83" data-mp="28fa4qmle0zkj">
        <div className="atom-hd" data-id="element-84" data-mp="24eqbvv7uki47">
          <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 6
        }} data-id="element-85" data-mp="24dyf4h2ikecc">
            <div className="t" data-id="element-86" data-mp="1zfdfojv35yoy">See how it works</div>
            <div className="st" data-id="element-87" data-mp="t0crj0cayksx">Pick a document to view how Synthia turns it into instructional data</div>
          </div>
          <div className="eyebrow" style={{
          letterSpacing: '.1em'
        }} data-id="element-88" data-mp="o0qf2t4at8l6">
            ATOMIZER
          </div>
        </div>

        <div className="pick-g" data-id="element-89" data-mp="pjgo9wmggm16">
          {SAMPLES.map(s => <button key={s.id} type="button" className={`pick${result?.id === s.id ? ' on' : ''}`} aria-pressed={result?.id === s.id} onClick={() => runSample(s.id)} data-id="element-90" data-mp="uh9sik4775yt">
              <span className="bd" data-id="element-91" data-mp="khrai97ulu9e">{s.badge}</span>
              <span className="tx" data-id="element-92" data-mp="6s7b9ddh7f4u">
                <span className="nm" data-id="element-93" data-mp="6r5padux988c">{s.name}</span>
                <span className="mt" data-id="element-94" data-mp="binsl8kf3kwh">{s.meta}</span>
              </span>
            </button>)}
        </div>

        <div className="pick-note" data-id="element-95" data-mp="3sznoft5nw157">
          Want it on your own document?{' '}
          <Link to="/contact" style={{
          textDecoration: 'underline',
          textUnderlineOffset: 3
        }} data-id="element-96" data-mp="1skz779dw4xm">
            Send us one
          </Link>{' '}
          and we’ll run it.
        </div>
        {message && <div className="atom-msg" role="status" data-id="element-97" data-mp="3oxal1bee0fbl">
            {message}
          </div>}
      </div>

      {result && <div className="ledger" aria-live="polite" key={result.id} data-id="element-98" data-mp="35ctndxswdjsk">
          <div className="ledger-hd" data-id="element-99" data-mp="30cxilwcdloby">
            <div style={{
          display: 'flex',
          alignItems: 'baseline',
          gap: 14,
          flexWrap: 'wrap'
        }} data-id="element-100" data-mp="30dz0okrt7b36">
              <div className="big" aria-label={`${total} rules found`} data-id="element-101" data-mp="3ed8b13tc3az8">
                {shownTotal}
              </div>
              <div className="lab" data-id="element-102" data-mp="3acy33llzeahq">RULES FOUND</div>
              {result.binding !== undefined && <div className="lab" style={{
            color: 'var(--amber)'
          }} data-id="element-103" data-mp="3o8jmnd9g422r">
                  {result.binding} binding · {plural(result.conflicts ?? 0, 'conflict', 'conflicts')} found
                </div>}
            </div>
            <div className="src" data-id="element-104" data-mp="3t1l33ppaw9og">{result.label}</div>
          </div>
          <div role="table" aria-label="Rules found" data-id="element-105" data-mp="3t3fpz9k4ubv6">
            <div className="lrow h" role="row" data-id="element-106" data-mp="awrnlrggw2t7">
              <span role="columnheader" data-id="element-107" data-mp="atdk2fw2hnk6">FORCE</span>
              <span role="columnheader" data-id="element-108" data-mp="5xoi65wkrukp">RULE</span>
              <span role="columnheader" data-id="element-109" data-mp="5usmypc43jzy">TRIGGER</span>
              <span role="columnheader" data-id="element-110" data-mp="20c2fk2q759pp">SOURCE</span>
              <span role="columnheader" data-id="element-111" data-mp="25byfnqvrwgzz">THEIR WORDS</span>
            </div>
            {result.rows.map((r, i) => <div className="lrow in" role="row" key={i} style={{
          animationDelay: `${i * 50}ms`
        }} data-id="element-112" data-mp="2a256zyho6flx" data-mpi={i}>
                <span role="cell" data-id="element-113" data-mp="2e4i98cx7jvdy">
                  <span className={`f ${FORCE_CLASS[r.force]}`} data-id="element-114" data-mp="2e3qqw3smfu5m">{r.force}</span>
                </span>
                <span role="cell" data-id="element-115" data-mp="1hfu58kibu8nj">
                  {r.rule}
                  {r.note && <span className="cf" data-id="element-116" data-mp="1me3z8rsbf37k">{r.note}</span>}
                </span>
                <span role="cell" className="tg" data-id="element-117" data-mp="1qe65zfcwqmtp">
                  {r.trigger}
                </span>
                <span role="cell" className="sc" data-id="element-118" data-mp="1veacqdo0k36q">
                  {r.source}
                </span>
                <span role="cell" className="qw" title={r.words} data-id="element-119" data-mp="31umcgj6hht4f">
                  {r.words}
                </span>
              </div>)}
            <div className="lcount" data-id="element-120" data-mp="36v0m25nqhb4k">
              {shown} of {total} shown
            </div>
          </div>

          <div data-id="element-121" data-mp="3gyp55kvvqogi">
            <div className="ledger-ft" data-id="element-122" data-mp="3by1fb2je0dnv">
              <div className="s" data-id="element-123" data-mp="3bz2rxbg1x87p">
                Showing {shown} of {total}. The rest have owners, versions, and triggers waiting.
              </div>
              {!libOpen && <button type="button" className="btn btn-s" style={{
            height: 44
          }} onClick={() => setLibOpen(true)} data-id="element-124" data-mp="33698d1tknmo1">
                  Add to your library
                </button>}
            </div>
            {libOpen && <div className="libform" data-id="element-125" data-mp="2t5yurg3mkd8c">
                <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 14
          }} data-id="element-126" data-mp="2o7xfl69bky25">
                  <div className="t" data-id="element-127" data-mp="2k6lkwmbl9mn3">Your library starts here.</div>
                  <div className="b" data-id="element-128" data-mp="2k8epzx6jzysz">
                    These {total} rules, versioned and owned by you, become the first entries. Tell us where to send the keys.
                  </div>
                  <div className="s" data-id="element-129" data-mp="ifqj9dupat8g">
                    What you see is the plain-English face of each rule. Underneath, it’s hashed, versioned, traced to its source, and finalized through twelve processes.
                  </div>
                </div>
                {send === 'sent' ? <div className="ok" role="status" data-id="element-130" data-mp="22ykpf1kpdxv1">
                    Got it. We’ll be in touch at {fields.email} with your keys.
                  </div> : <form onSubmit={e => {
            e.preventDefault();
            submitLibrary(fields);
          }} data-id="element-131" data-mp="2uo76xkrap8fd">
                    {([['name', 'Name', 'text'], ['email', 'Work email', 'email'], ['company', 'Company', 'text']] as const).map(([key, label, type]) => <React.Fragment key={key} data-id="element-132" data-mp="1f888503kiors">
                        <label htmlFor={`lib-${key}`} className="sr" data-id="element-133" data-mp="1f5lymux49z6x">
                          {label}
                        </label>
                        <input id={`lib-${key}`} className="inp" type={type} required placeholder={label} value={fields[key]} onChange={e => setFields(f => ({
                ...f,
                [key]: e.target.value
              }))} data-id="element-134" data-mp="12pcs5g9qo82w" />
                      </React.Fragment>)}
                    <button className="btn btn-s" type="submit" disabled={send === 'sending'} style={{
              justifyContent: 'center',
              marginTop: 4
            }} data-id="element-135" data-mp="iz0s17737oae">
                      {send === 'sending' ? 'Sending…' : 'Start my library'}
                    </button>
                    {send === 'error' && <div className="err" role="alert" data-id="element-136" data-mp="57m7ux1g1h1r">
                        That didn’t go through. Try again, or email hello@meetsynthia.ai.
                      </div>}
                  </form>}
              </div>}
          </div>
        </div>}
    </div>;
}